import { useCallback, useMemo, useRef, useState } from 'react'
import type { Data, Permissions } from '@puckeditor/core'
import {
  BreakpointId,
  DEFAULT_BREAKPOINT,
  isOverrideBreakpoint,
} from './breakpoints'
import { resolveResponsive } from './resolve'
import { diffPropOverrides } from './diff'
import type { ResponsiveData } from './types'

/** Structure is locked outside Default — only field edits allowed. */
const LOCKED_PERMISSIONS: Partial<Permissions> = {
  drag: false,
  duplicate: false,
  delete: false,
  insert: false,
  edit: true,
}

export interface UseResponsivePuck {
  activeBp: BreakpointId
  /** Switch the breakpoint being edited (folds current edits first). */
  setActiveBp: (bp: BreakpointId) => void
  /** `key` for <Puck> — remounts on breakpoint switch / external load. */
  puckKey: string
  /** Resolved (un-pruned) data to feed <Puck> for the active breakpoint. */
  puckData: Data
  /** `permissions` for <Puck> — locked on non-default breakpoints. */
  permissions: Partial<Permissions> | undefined
  /** Pass to <Puck onChange>. */
  onChange: (edited: Data) => void
  /** Full ResponsiveData with latest edits folded in (publish / preview). */
  getData: () => ResponsiveData
  /** Replace the source (external load) and reset to Default. */
  load: (data: ResponsiveData) => void
}

/**
 * Owns the responsive editing model on top of Puck:
 * - keeps one source `ResponsiveData` (base + sparse overrides),
 * - feeds Puck the resolved tree for the active breakpoint,
 * - folds `onChange` edits back as deltas (base on Default, `diff` elsewhere),
 * - locks structure permissions outside Default.
 * Reusable across sites — the editor page just wires it to <Puck>.
 */
export function useResponsivePuck(initial: ResponsiveData): UseResponsivePuck {
  const [source, setSource] = useState<ResponsiveData>(initial)
  const [activeBp, setActiveBpState] = useState<BreakpointId>(DEFAULT_BREAKPOINT)
  const [reloadKey, setReloadKey] = useState(0)
  const editedRef = useRef<Data | null>(null)

  const fold = useCallback(
    (src: ResponsiveData, bp: BreakpointId, edited: Data | null): ResponsiveData => {
      if (!edited) return src
      if (bp === DEFAULT_BREAKPOINT) {
        // Default edits the base tree directly.
        return {
          ...src,
          root: edited.root,
          content: edited.content,
          ...(edited.zones ? { zones: edited.zones } : {}),
        }
      }
      // Non-default: store only prop deltas vs the base tree.
      if (!isOverrideBreakpoint(bp)) return src
      const base = resolveResponsive(src, DEFAULT_BREAKPOINT, { prune: false })
      const props = diffPropOverrides(base, edited)
      const responsive = { ...(src.responsive ?? {}) }
      if (Object.keys(props).length) {
        responsive[bp] = { props }
      } else {
        delete responsive[bp]
      }
      return { ...src, responsive }
    },
    [],
  )

  const onChange = useCallback((edited: Data) => {
    editedRef.current = edited
  }, [])

  const setActiveBp = useCallback(
    (next: BreakpointId) => {
      if (next === activeBp) return
      setSource(prev => fold(prev, activeBp, editedRef.current))
      editedRef.current = null
      setActiveBpState(next)
    },
    [activeBp, fold],
  )

  const getData = useCallback(
    (): ResponsiveData => fold(source, activeBp, editedRef.current),
    [source, activeBp, fold],
  )

  const load = useCallback((data: ResponsiveData) => {
    setSource(data)
    editedRef.current = null
    setActiveBpState(DEFAULT_BREAKPOINT)
    setReloadKey(k => k + 1)
  }, [])

  const puckData = useMemo(
    () => resolveResponsive(source, activeBp, { prune: false }),
    [source, activeBp],
  )

  // Memoize the returned API so consumers (and their useCallback/useEffect deps)
  // don't churn on every render — only when something actually changed.
  return useMemo(
    () => ({
      activeBp,
      setActiveBp,
      puckKey: `${reloadKey}-${activeBp}`,
      puckData,
      permissions: isOverrideBreakpoint(activeBp) ? LOCKED_PERMISSIONS : undefined,
      onChange,
      getData,
      load,
    }),
    [activeBp, setActiveBp, reloadKey, puckData, onChange, getData, load],
  )
}
